// WidgetEmbedPanel — hand-built Embed override-slot for the Widget Studio
// instance detail (CHG-2473 / ITER-905216, Widget Studio S6).
//
// Injected into the generated widget-instance-detail.ts `tab-embed`
// override-slot (override_slot: true, slot_component: WidgetEmbedPanel in the
// entity-hint). Calls POST /admin/widgets/instances/{id}/embed-code to generate
// the embed snippet and renders it with copy-to-clipboard.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { widgetsAdminApi } from "../../api/widgets-admin";

export function WidgetEmbedPanel({ instanceId }: { instanceId: string }) {
  const [copied, setCopied] = useState(false);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["widget-instances", "embed", instanceId],
    queryFn: () => widgetsAdminApi.embedCode(instanceId),
  });

  const onCopy = async () => {
    const code = data?.embed_code ?? "";
    if (!code) return;
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      } catch {
        // clipboard blocked (insecure context / permissions) — no-op; the
        // snippet remains visible for manual selection.
      }
    }
  };

  if (isLoading) {
    return (
      <div className="card" style={{ padding: 16, color: "var(--color-text-muted)" }}>
        Generating embed code…
      </div>
    );
  }
  if (isError || !data?.embed_code) {
    return (
      <div className="card" style={{ padding: 16, color: "var(--color-error)" }}>
        Could not generate the embed code.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
          Paste this snippet into your site where the widget should appear.
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCopy}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: 14,
          fontSize: 12,
          lineHeight: 1.6,
          fontFamily: "var(--font-mono, monospace)",
          background: "var(--color-bg-subtle)",
          color: "var(--color-text-secondary)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: 8,
          overflowX: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        <code>{data.embed_code}</code>
      </pre>
    </div>
  );
}
